import React from "react";
import { buildPlanningOrderEditPresentation } from "../utils/planningOrderEditDiagnostics.js";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  Copy,
  Download,
  Filter,
  LoaderCircle,
  RefreshCcw,
  Trash2,
  UserRound,
} from "lucide-react";
import {
  clearDispatchAlertCaptures,
  fetchDispatchAlertCaptureConfig,
  fetchDispatchAlertCaptureImage,
  fetchDispatchAlertCaptures,
  fetchDispatchAlertCaptureTargets,
} from "../lib/systemConfigApi.js";
import useDispatchCaptureArrivalHighlight from "../hooks/useDispatchCaptureArrivalHighlight.js";
import {
  buildCaptureCampaignFilterOptions,
  buildCaptureUserFilterOptions,
  groupDispatchAlertCaptures,
  normalizeCaptureFacetUsers,
} from "../lib/dispatchAlertCaptureAdminModel.js";
import { normalizeDispatchAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";
import {
  buildAlertCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME,
  DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import { publishDispatchCaptureArrival } from "../lib/dispatchCaptureStatusEvents.js";
import {
  formatCaptureActivityGroupLabel,
  groupCaptureItemsByActivityDate,
} from "../utils/dispatchCaptureListGroups.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import { formatPlanningDiagnosticActionLabel } from "../utils/planningLastDiagnostics.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import DispatchCaptureDispatchGroupHeader from "./DispatchCaptureDispatchGroupHeader.jsx";
import DispatchCaptureHistorySwitcher from "./DispatchCaptureHistorySwitcher.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";

const SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("system_config");
const ALERT_TYPES = [
  { label: "Échec du dispatch", value: "dispatch_failure" },
  { label: "Notification d’erreur", value: "global_error" },
  { label: "Non-conformité", value: "non_compliance" },
];
const ALERT_TYPE_LABELS = Object.fromEntries(ALERT_TYPES.map((entry) => [entry.value, entry.label]));
const DISPATCH_PERIOD_LABELS = {
  day: "Jour",
  evening: "Soir",
  jour: "Jour",
  soir: "Soir",
};
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
const COMPACT_PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  buttonToneClasses.primary,
]);
const COMPACT_SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-full border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  buttonToneClasses.ghost,
]);
const COMPACT_DANGER_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-1.5 rounded-full border font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.pill,
  "border-rose-600 bg-rose-600 hover:bg-rose-700",
]);
const CAPTURE_THUMBNAIL_SIZE_CLASS_NAME = "h-[4.5rem] w-52 shrink-0 max-[1180px]:w-36 max-[720px]:w-24";

function formatCaptureDateTimeParts(value) {
  if (!value) {
    return {
      dateLabel: "Date indisponible",
      dateTimeLabel: "Date et heure indisponibles",
      isoValue: undefined,
      timeLabel: "Heure indisponible",
    };
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      dateLabel: "Date indisponible",
      dateTimeLabel: "Date et heure indisponibles",
      isoValue: undefined,
      timeLabel: "Heure indisponible",
    };
  }

  const dateLabel = new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" }).format(parsedDate);
  const timeLabel = new Intl.DateTimeFormat("fr-CA", { timeStyle: "medium" }).format(parsedDate);

  return {
    dateLabel,
    dateTimeLabel: `${dateLabel}, ${timeLabel}`,
    isoValue: parsedDate.toISOString(),
    timeLabel,
  };
}

function formatDispatchDate(value) {
  if (!value) {
    return "Journée non précisée";
  }

  const parsedDate = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "long" }).format(parsedDate);
}

function formatBytes(value) {
  const sizeBytes = Number(value);
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return "";
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} o`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} Ko`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function resolveCaptureContextTruckLabel(truck, { includeId = true } = {}) {
  if (!truck || typeof truck !== "object") {
    return "";
  }
  const label = typeof truck.label === "string" ? truck.label.trim() : "";
  const id = typeof truck.id === "string" ? truck.id.trim() : "";
  return includeId && label && id && label !== id ? `${label} (${id})` : label || id;
}

function formatCaptureContextIdentifiers(values, count, singularLabel, pluralLabel) {
  const identifiers = Array.isArray(values) ? values.filter(Boolean) : [];
  const total = Math.max(Number(count) || 0, identifiers.length);
  if (!identifiers.length && total === 0) {
    return "";
  }
  if (!identifiers.length) {
    return `${total} ${total > 1 ? pluralLabel.toLocaleLowerCase("fr-CA") : singularLabel.toLocaleLowerCase("fr-CA")}`;
  }
  const visibleIdentifiers = identifiers.slice(0, 6);
  const hiddenCount = Math.max(0, total - visibleIdentifiers.length);
  return `${total > 1 ? pluralLabel : singularLabel} ${visibleIdentifiers.join(", ")}${hiddenCount ? ` + ${hiddenCount}` : ""}`;
}

function resolveCaptureContextActionLabel(context) {
  const explicitLabel = typeof context?.action_label === "string" ? context.action_label.trim() : "";
  const actionLabel = explicitLabel || formatPlanningDiagnosticActionLabel(context?.action || "");
  const interactionOrigin = typeof context?.interaction_origin === "string"
    ? context.interaction_origin.trim().toLocaleLowerCase("fr-CA")
    : "";
  if (["undo", "revert"].includes(interactionOrigin) && actionLabel) {
    return `Revenir · ${actionLabel}`;
  }
  return actionLabel;
}

function buildCaptureContextPresentation(rawContext) {
  const context = normalizeDispatchAlertCaptureContext(rawContext);
  if (!context) {
    return null;
  }

  const entities = context.entities || {};
  const dispatch = context.dispatch || {};
  const technical = context.technical || {};
  const actionLabel = resolveCaptureContextActionLabel(context);
  const sourceTruckLabel = resolveCaptureContextTruckLabel(entities.source_truck);
  const targetTruckLabel = resolveCaptureContextTruckLabel(entities.target_truck);
  const currentTruckLabel = resolveCaptureContextTruckLabel(entities.current_truck);
  const compactSourceTruckLabel = resolveCaptureContextTruckLabel(entities.source_truck, { includeId: false });
  const compactTargetTruckLabel = resolveCaptureContextTruckLabel(entities.target_truck, { includeId: false });
  const compactCurrentTruckLabel = resolveCaptureContextTruckLabel(entities.current_truck, { includeId: false });
  const compactTruckLabel = compactSourceTruckLabel && compactTargetTruckLabel && compactSourceTruckLabel !== compactTargetTruckLabel
    ? `${compactSourceTruckLabel} → ${compactTargetTruckLabel}`
    : compactTargetTruckLabel || compactSourceTruckLabel || compactCurrentTruckLabel;
  const compactOrderLabel = formatCaptureContextIdentifiers(
    entities.order_ids,
    entities.order_count,
    "Commande",
    "Commandes",
  );
  const compactDeliveryLabel = !compactOrderLabel
    ? formatCaptureContextIdentifiers(
      entities.delivery_ids,
      entities.delivery_count,
      "Livraison",
      "Livraisons",
    )
    : "";
  const subjectLabel = typeof context.subject?.label === "string" ? context.subject.label.trim() : "";
  const subjectId = typeof context.subject?.id === "string" ? context.subject.id.trim() : "";
  const compactSubjectLabel = compactOrderLabel || compactDeliveryLabel || subjectId || subjectLabel;
  const surfaceLabels = {
    non_compliance: "Non-conformité",
    status_notice: "Bandeau d’échec",
    toast: "Notification",
  };
  const summary = [actionLabel, compactTruckLabel, compactSubjectLabel].filter(Boolean).join(" · ")
    || `${surfaceLabels[context.surface] || context.source || "Contexte"} · Action non précisée`;

  const originLabels = {
    direct: "Action directe",
    undo: "Revenir",
    revert: "Revenir",
  };
  const rebuildModeLabels = {
    complete: "Complet",
    fast: "Rapide",
  };
  const failureKindLabels = {
    http: "Réponse HTTP",
    invalid_response: "Réponse serveur incomplète",
    network: "Réseau",
    request: "Requête",
    status_notice: "Traitement asynchrone",
    validation: "Validation",
  };
  const editPresentation = buildPlanningOrderEditPresentation({ action: context.action, diagnostics: {
    order_edit_details: context.order_edit?.canonical, order_edit_submission: context.order_edit?.submission, order_edit_failure: context.order_edit?.failure,
  } });
  const circumstances = [
    ...(editPresentation ? [...editPresentation.lines, ...editPresentation.notices].map((value) => ({ label: "Modification Gantt", value })) : []),
    actionLabel ? { label: "Action", value: actionLabel } : null,
    context.source ? { label: "Source", value: context.source } : null,
    context.surface ? { label: "Surface", value: surfaceLabels[context.surface] || context.surface } : null,
    context.interaction_origin
      ? { label: "Origine", value: originLabels[context.interaction_origin] || context.interaction_origin }
      : null,
    subjectLabel || subjectId
      ? { label: "Sujet", value: [subjectLabel, subjectId && subjectId !== subjectLabel ? subjectId : ""].filter(Boolean).join(" · ") }
      : null,
    Array.isArray(entities.order_ids) && entities.order_ids.length
      ? { label: "Commandes", value: formatCaptureContextIdentifiers(entities.order_ids, entities.order_count, "Commande", "Commandes") }
      : null,
    Array.isArray(entities.delivery_ids) && entities.delivery_ids.length
      ? { label: "Livraisons", value: formatCaptureContextIdentifiers(entities.delivery_ids, entities.delivery_count, "Livraison", "Livraisons") }
      : null,
    Array.isArray(entities.loading_step_ids) && entities.loading_step_ids.length
      ? { label: "Étapes / groupe", value: formatCaptureContextIdentifiers(entities.loading_step_ids, entities.loading_step_ids.length, "Étape", "Étapes") }
      : null,
    sourceTruckLabel ? { label: "Camion source", value: sourceTruckLabel } : null,
    targetTruckLabel ? { label: "Camion cible", value: targetTruckLabel } : null,
    currentTruckLabel ? { label: "Camion courant", value: currentTruckLabel } : null,
    dispatch.rebuild_mode
      ? { label: "Mode de recalcul", value: rebuildModeLabels[dispatch.rebuild_mode] || dispatch.rebuild_mode }
      : null,
    typeof dispatch.protect_after_reassignment === "boolean"
      ? { label: "Protection après réaffectation", value: dispatch.protect_after_reassignment ? "Oui" : "Non" }
      : null,
    typeof dispatch.allow_overtime === "boolean"
      ? { label: "Heures supplémentaires", value: dispatch.allow_overtime ? "Autorisées" : "Non autorisées" }
      : null,
    dispatch.source_date ? { label: "Date source", value: dispatch.source_date } : null,
    dispatch.target_date ? { label: "Date cible", value: dispatch.target_date } : null,
    dispatch.target_period
      ? { label: "Période cible", value: DISPATCH_PERIOD_LABELS[dispatch.target_period] || dispatch.target_period }
      : null,
    ...(Array.isArray(context.details)
      ? context.details.map((detail) => ({ label: detail.label, value: detail.value }))
      : []),
  ].filter((item) => item?.label && item?.value);
  const endpoint = [technical.method, technical.path].filter(Boolean).join(" ");
  const browserTransport = context.mutation_transport;
  const serverTransport = context.server_response_transport;
  const revision = context.planning_revision || {};
  const refreshLabels = { not_needed: "Non nécessaire", pending: "En cours", succeeded: "Réussie", failed: "Échouée",
    missing_revision: "Révision absente", context_changed: "Contexte changé", assignment_changed: "Affectation modifiée",
    truck_missing: "Camion absent", planning_replaced: "Planning remplacé pendant la lecture", invalid_response: "Réponse incohérente" };
  const technicalRows = [
    ...[["initial_runtime_revision", "Version initiale"], ["expected_runtime_revision", "Version envoyée"],
      ["current_runtime_revision", "Version serveur au refus"], ["received_runtime_revision", "Dernière version reçue"],
      ["installed_runtime_revision", "Version affichée au constat"], ["expected_assignment_version", "Version de l’affectation envoyée"],
      ["request_id", "Requête d’affectation"]]
      .filter(([key]) => revision[key] != null).map(([key, label]) => ({ label, value: String(revision[key]) })),
    revision.revision_cause ? { label: "Cause du changement de version", value: revision.revision_cause.state === "exact_chain"
      ? "Chaîne de versions reliée aux audits serveur" : "Non tracée : la cause ne peut pas être établie avec certitude" } : null,
    ...(revision.revision_cause?.transitions || []).map(edge => ({ label: "Changement confirmé",
      value: `${edge.stage === "source_invalidation" ? "Modification source et invalidation" : "Modification du planning"} · ${edge.actions.join(", ")} · ${edge.source_kinds.join(", ")} · ${edge.truck_ids.join(", ")}${edge.truck_count > edge.truck_ids.length ? ` (${edge.truck_count} camions, extrait)` : ""} · ${edge.changed_fields.join(", ")} · ${edge.before_revision} → ${edge.after_revision} · Audits : ${edge.audit_ids.join(", ")}${edge.audit_count > edge.audit_ids.length ? ` (${edge.audit_count} audits, extrait)` : ""}` })),
    ...(revision.events || []).map(event => ({ label: `Version ${{ received: "reçue", applied: "affichée", ignored: "ignorée" }[event.outcome]}`,
      value: `${event.at || ""} · ${event.action || ""} · ${event.runtime_revision || "absente"}${event.reason ? ` · ${event.reason}` : ""}${event.request_id ? ` · Requête : ${event.request_id}` : ""}` })),
    ...(context.validation_errors || []).map(({ field, code }) => ({ label: "Champ refusé (422)", value: `${field} · ${code}` })),
    ...[["initial_runtime_revision_present", "Révision avant actualisation"],
      ["expected_runtime_revision_present", "Révision requise dans la requête"],
      ["expected_assignment_version_present", "Version d’affectation initiale"],
      ["result_runtime_revision_present", "Révision dans la réponse"]]
      .filter(([key]) => typeof revision[key] === "boolean")
      .map(([key, label]) => ({ label, value: revision[key] ? "Présente" : "Absente" })),
    revision.refresh_outcome ? { label: "Actualisation du planning", value: refreshLabels[revision.refresh_outcome] || revision.refresh_outcome } : null,
    revision.initial_planning_operation ? { label: "Planning au début de l’opération", value: revision.initial_planning_operation } : null,
    revision.last_planning_operation ? { label: "Dernier remplacement du planning", value: revision.last_planning_operation } : null,
    ...(editPresentation?.diagnosticRows || []),
    browserTransport ? { label: "Réponse navigateur", value: `${browserTransport.http_status ?? "Aucun statut reçu"} · ${browserTransport.phase || "inconnue"} · ${browserTransport.content_kind || "type inconnu"}` } : null,
    browserTransport?.loaded_asset_version ? { label: "Version du navigateur", value: browserTransport.loaded_asset_version } : null,
    browserTransport?.railway_request_id ? { label: "Requête Railway reçue", value: browserTransport.railway_request_id } : null,
    serverTransport ? { label: "Réponse serveur", value: `HTTP ${serverTransport.status_code ?? "inconnu"} · ${serverTransport.body_complete ? "Envoi ASGI terminé" : "Envoi ASGI incomplet"} · ${serverTransport.bytes_sent ?? 0} octets` } : null,
    serverTransport?.request_id ? { label: "Requête serveur", value: serverTransport.request_id } : null,
    browserTransport && !serverTransport ? { label: "Trace serveur", value: "Non disponible dans cette capture" } : null,
    endpoint ? { label: "Endpoint", value: endpoint } : null,
    technical.http_status ? { label: "Statut HTTP", value: String(technical.http_status) } : null,
    technical.reason_code ? { label: "Code", value: technical.reason_code } : null,
    technical.failure_kind
      ? { label: "Nature", value: failureKindLabels[technical.failure_kind] || technical.failure_kind }
      : null,
    technical.failed_stage ? { label: "Étape échouée", value: technical.failed_stage } : null,
    technical.correlation_id ? { label: "Corrélation", value: technical.correlation_id } : null,
    context.operation_id ? { label: "Opération", value: context.operation_id } : null,
  ].filter(Boolean);

  return { circumstances, context, summary, technicalRows };
}

function buildCaptureContextClipboardText({
  alertText,
  alertTypeLabel,
  capture,
  captureDateTime,
  dispatchLabel,
  presentation,
  userLabel,
}) {
  const formatRows = (rows) => rows.map((row) => `- ${row.label} : ${row.value}`);
  return [
    "Capture d’erreur Dispatch",
    `Heure : ${captureDateTime.dateTimeLabel}`,
    `Utilisateur : ${userLabel}`,
    `Dispatch : ${dispatchLabel}`,
    `Type : ${alertTypeLabel}`,
    ...(capture.campaign_id ? [`Campagne : ${capture.campaign_id}`] : []),
    `Message : ${alertText}`,
    "",
    `Résumé : ${presentation.summary}`,
    "",
    "Circonstances",
    ...formatRows(presentation.circumstances),
    ...(presentation.technicalRows.length
      ? ["", "Diagnostic technique", ...formatRows(presentation.technicalRows)]
      : []),
    "", "Contexte structuré", JSON.stringify(presentation.context, null, 2),
  ].join("\n");
}

async function copyCaptureContextText(value) {
  if (globalThis.navigator?.clipboard?.writeText) {
    await globalThis.navigator.clipboard.writeText(value);
    return;
  }
  if (typeof document === "undefined" || typeof document.execCommand !== "function") {
    throw new Error("Copie indisponible");
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const didCopy = document.execCommand("copy");
  textarea.remove();
  if (!didCopy) {
    throw new Error("Copie indisponible");
  }
}

function normalizeTargetUsers(payload) {
  if (!Array.isArray(payload?.users)) {
    return [];
  }

  return payload.users
    .filter((user) => typeof user?.user_id === "string" && user.user_id.trim())
    .map((user) => ({
      email: typeof user.email === "string" ? user.email.trim() : "",
      label: (
        typeof user.display_name === "string" && user.display_name.trim()
          ? user.display_name.trim()
          : typeof user.email === "string" && user.email.trim()
            ? user.email.trim()
            : user.user_id.trim()
      ),
      userId: user.user_id.trim(),
    }));
}

function sanitizeFilenamePart(value, fallbackValue) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  return (normalizedValue || fallbackValue)
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || fallbackValue;
}

function buildCaptureFilename(capture) {
  const datePart = sanitizeFilenamePart(capture?.dispatch_date, "sans-date");
  const userPart = sanitizeFilenamePart(capture?.user_email, "utilisateur");
  const typePart = sanitizeFilenamePart(capture?.alert_type, "alerte");
  return `alerte-dispatch-${datePart}-${userPart}-${typePart}.png`;
}

function CaptureImage({ capture }) {
  const [shouldLoadImage, setShouldLoadImage] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState("");
  const [imageError, setImageError] = React.useState("");
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [previewStyle, setPreviewStyle] = React.useState(null);
  const imageHostRef = React.useRef(null);
  const previewHintId = React.useId();
  const isPreviewVisible = Boolean(imageUrl) && (isHovered || isFocused);

  const updatePreviewPosition = React.useCallback(() => {
    const imageHost = imageHostRef.current;
    if (!imageHost || typeof window === "undefined") {
      return;
    }

    const anchorRect = imageHost.getBoundingClientRect();
    const viewportPadding = 12;
    const previewGap = 10;
    const viewportWidth = Math.max(0, window.innerWidth);
    const viewportHeight = Math.max(0, window.innerHeight);
    const availableWidth = Math.max(1, viewportWidth - (viewportPadding * 2));
    const availableAbove = Math.max(0, anchorRect.top - previewGap - viewportPadding);
    const availableBelow = Math.max(0, viewportHeight - anchorRect.bottom - previewGap - viewportPadding);
    const availableVerticalSide = Math.max(availableAbove, availableBelow);
    const sourceWidth = Math.max(1, Number(capture.width) || 320);
    const sourceHeight = Math.max(1, Number(capture.height) || 180);
    const aspectRatio = Math.min(5, Math.max(0.35, sourceWidth / sourceHeight));

    let previewWidth = Math.min(
      availableWidth,
      560,
      Math.max(360, anchorRect.width * 2.5),
    );
    let previewHeight = previewWidth / aspectRatio;
    const maximumPreviewHeight = Math.min(480, availableVerticalSide || viewportHeight - (viewportPadding * 2));

    if (previewHeight > maximumPreviewHeight) {
      previewHeight = Math.max(1, maximumPreviewHeight);
      previewWidth = Math.min(availableWidth, previewHeight * aspectRatio);
    }

    const left = Math.max(
      viewportPadding,
      Math.min(anchorRect.left, viewportWidth - previewWidth - viewportPadding),
    );
    const shouldPlaceBelow = availableBelow >= previewHeight || availableBelow >= availableAbove;
    const top = shouldPlaceBelow
      ? Math.min(anchorRect.bottom + previewGap, viewportHeight - previewHeight - viewportPadding)
      : Math.max(viewportPadding, anchorRect.top - previewGap - previewHeight);

    setPreviewStyle({
      height: `${Math.round(previewHeight)}px`,
      left: `${Math.round(left)}px`,
      top: `${Math.round(Math.max(viewportPadding, top))}px`,
      width: `${Math.round(previewWidth)}px`,
    });
  }, [capture.height, capture.width]);

  React.useEffect(() => {
    const imageHost = imageHostRef.current;
    const IntersectionObserverImpl = typeof window !== "undefined"
      ? window.IntersectionObserver
      : undefined;

    if (!imageHost || typeof IntersectionObserverImpl !== "function") {
      setShouldLoadImage(true);
      return undefined;
    }

    const observer = new IntersectionObserverImpl((entries) => {
      if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
        return;
      }
      observer.disconnect();
      setShouldLoadImage(true);
    }, {
      rootMargin: "480px 0px",
    });
    observer.observe(imageHost);

    return () => observer.disconnect();
  }, [capture.id]);

  React.useEffect(() => {
    if (!shouldLoadImage) {
      return undefined;
    }

    let isCancelled = false;
    let nextObjectUrl = "";
    setImageUrl("");
    setImageError("");

    void fetchDispatchAlertCaptureImage(capture.id)
      .then((imageBlob) => {
        if (isCancelled) {
          return;
        }
        nextObjectUrl = URL.createObjectURL(imageBlob);
        setImageUrl(nextObjectUrl);
      })
      .catch((error) => {
        if (!isCancelled) {
          setImageError(error instanceof Error ? error.message : "Impossible de charger l’image.");
        }
      });

    return () => {
      isCancelled = true;
      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [capture.id, shouldLoadImage]);

  React.useEffect(() => {
    if (!isPreviewVisible) {
      return undefined;
    }

    updatePreviewPosition();
    window.addEventListener("resize", updatePreviewPosition);
    window.addEventListener("scroll", updatePreviewPosition, true);

    return () => {
      window.removeEventListener("resize", updatePreviewPosition);
      window.removeEventListener("scroll", updatePreviewPosition, true);
    };
  }, [isPreviewVisible, updatePreviewPosition]);

  if (!shouldLoadImage) {
    return (
      <div
        className={joinClasses([
          CAPTURE_THUMBNAIL_SIZE_CLASS_NAME,
          "flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-2 text-center text-[0.68rem] font-medium text-slate-500",
        ])}
        data-dispatch-alert-capture-image-pending="true"
        data-dispatch-alert-capture-thumbnail="true"
        ref={imageHostRef}
      >
        PNG à charger
      </div>
    );
  }

  if (imageError) {
    return (
      <div
        className={joinClasses([
          CAPTURE_THUMBNAIL_SIZE_CLASS_NAME,
          "grid place-items-center rounded-xl border border-rose-200 bg-rose-50 px-2 text-center text-[0.68rem] font-medium text-rose-700",
        ])}
        data-dispatch-alert-capture-thumbnail="true"
        role="alert"
      >
        {imageError}
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div
        className={joinClasses([
          CAPTURE_THUMBNAIL_SIZE_CLASS_NAME,
          "flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 text-[0.68rem] font-medium text-slate-600",
        ])}
        data-dispatch-alert-capture-thumbnail="true"
      >
        <LoaderCircle className="animate-spin" size={16} aria-hidden="true" />
        PNG…
      </div>
    );
  }

  return (
    <>
      <a
        aria-describedby={previewHintId}
        className={joinClasses([
          CAPTURE_THUMBNAIL_SIZE_CLASS_NAME,
          "block overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition hover:border-slate-400 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-500",
        ])}
        data-dispatch-alert-capture-thumbnail="true"
        href={imageUrl}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={imageHostRef}
        rel="noreferrer"
        target="_blank"
        title="Ouvrir l’image à sa taille réelle"
      >
        <img
          alt={`Capture de l’alerte ${ALERT_TYPE_LABELS[capture.alert_type] || capture.alert_type || "Dispatch"}`}
          className="h-full w-full object-contain object-top"
          height={Number(capture.height) || undefined}
          loading="lazy"
          src={imageUrl}
          width={Number(capture.width) || undefined}
        />
      </a>
      <span className="sr-only" id={previewHintId}>
        Un aperçu agrandi s’affiche au survol ou au focus. Activez ce lien pour ouvrir le PNG à sa taille réelle.
      </span>
      {isPreviewVisible && previewStyle && getReactPortalRoot() instanceof HTMLElement
        ? createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed z-[90] overflow-hidden rounded-2xl border border-slate-300 bg-white p-2 shadow-2xl"
            data-dispatch-alert-capture-preview="true"
            style={previewStyle}
          >
            <img
              alt=""
              className="h-full w-full object-contain object-top"
              src={imageUrl}
            />
          </div>,
          getReactPortalRoot(),
        )
        : null}
    </>
  );
}

function AlertCaptureCard({ capture, isDownloading, isNew = false, onDownload }) {
  const [copyStatus, setCopyStatus] = React.useState("idle");
  const alertTypeLabel = ALERT_TYPE_LABELS[capture.alert_type] || capture.alert_type || "Alerte Dispatch";
  const captureSize = formatBytes(capture.size_bytes);
  const dispatchPeriod = typeof capture.dispatch_period === "string" ? capture.dispatch_period.trim() : "";
  const dispatchPeriodLabel = DISPATCH_PERIOD_LABELS[dispatchPeriod.toLocaleLowerCase("fr-CA")] || dispatchPeriod;
  const groupedCaptureCount = Math.max(1, Number(capture.grouped_capture_count) || 1);
  const alertText = typeof capture.alert_text === "string" && capture.alert_text.trim()
    ? capture.alert_text.trim()
    : "Message non disponible";
  const userLabel = capture.user_email || capture.user_id || "Utilisateur inconnu";
  const dispatchLabel = [
    formatDispatchDate(capture.dispatch_date),
    dispatchPeriodLabel,
  ].filter(Boolean).join(" · ");
  const captureDateTime = formatCaptureDateTimeParts(capture.occurred_at || capture.created_at);
  const contextPresentation = buildCaptureContextPresentation(capture.capture_context);
  const handleCopyContext = async () => {
    if (!contextPresentation) {
      return;
    }
    try {
      await copyCaptureContextText(buildCaptureContextClipboardText({
        alertText,
        alertTypeLabel,
        capture,
        captureDateTime,
        dispatchLabel,
        presentation: contextPresentation,
        userLabel,
      }));
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };

  return (
    <article
      className={joinClasses([
        "flex min-h-[5.5rem] max-w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-2 overflow-hidden rounded-[4px] border border-black/6 bg-white px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.04)]",
        isNew ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME : "",
      ])}
      data-dispatch-alert-capture-card="true"
      data-dispatch-capture-arrival={isNew ? "true" : undefined}
    >
      <CaptureImage capture={capture} />
      <header
        className="grid min-w-48 flex-[1_1_20rem] gap-1 overflow-hidden text-[0.68rem] text-slate-600"
        data-dispatch-alert-capture-summary-line="true"
      >
        <div className="flex min-w-0 items-start gap-2">
          <span
            className="line-clamp-2 min-w-0 flex-1 whitespace-normal break-words text-[0.74rem] font-medium leading-[1.05rem] text-slate-800"
            data-dispatch-alert-capture-text="true"
            title={alertText}
          >
            {alertText}
          </span>
          {groupedCaptureCount > 1 ? (
            <span
              className="shrink-0 rounded-[4px] bg-slate-100 px-2 py-0.5 font-semibold text-slate-600"
              title={`${groupedCaptureCount} captures quasi simultanées regroupées pour cet incident`}
            >
              {groupedCaptureCount} regroupées
            </span>
          ) : null}
        </div>
        {contextPresentation ? (
          <p
            className="m-0 line-clamp-2 text-[0.67rem] font-semibold leading-4 text-[var(--color-brand)]"
            data-dispatch-alert-capture-context-summary="true"
            title={contextPresentation.summary}
          >
            {contextPresentation.summary}
          </p>
        ) : (
          <p
            className="m-0 text-[0.64rem] font-medium leading-4 text-slate-400"
            data-dispatch-alert-capture-context-missing="true"
          >
            Contexte non collecté pour cette capture
          </p>
        )}
      </header>
      <time
        aria-label={`Date et heure de la capture : ${captureDateTime.dateTimeLabel}`}
        className="flex w-[7.5rem] min-w-0 shrink-0 flex-col justify-center gap-1 border-l border-slate-200 pl-3 leading-none text-slate-600 max-[720px]:w-[5.75rem] max-[720px]:pl-2"
        data-dispatch-alert-capture-datetime-column="true"
        dateTime={captureDateTime.isoValue}
        title={captureDateTime.dateTimeLabel}
      >
        <span
          className="truncate text-[0.68rem] font-semibold text-slate-800"
          data-dispatch-alert-capture-date="true"
        >
          {captureDateTime.dateLabel}
        </span>
        <span
          className="truncate text-[0.64rem] tabular-nums text-slate-500"
          data-dispatch-alert-capture-time="true"
        >
          {captureDateTime.timeLabel}
        </span>
      </time>
      <div
        className="grid w-[clamp(10rem,18vw,16rem)] min-w-0 shrink-0 gap-1 text-[0.64rem] leading-none text-slate-600"
        data-dispatch-alert-capture-metadata-column="true"
      >
        <span className="inline-flex min-w-0 items-center gap-1" title={userLabel}>
          <UserRound className="shrink-0" size={11} aria-hidden="true" />
          <span className="truncate">{userLabel}</span>
        </span>
        <span className="inline-flex min-w-0 items-center gap-1" title={dispatchLabel}>
          <CalendarDays className="shrink-0" size={11} aria-hidden="true" />
          <span className="truncate">{dispatchLabel}</span>
        </span>
        <span className="flex min-w-0 items-center gap-2">
          {capture.campaign_id ? (
            <span className="min-w-0 truncate font-mono" title={capture.campaign_id}>
              Campagne {capture.campaign_id.slice(0, 8)}
            </span>
          ) : null}
          {captureSize ? (
            <span className="shrink-0">
              {capture.width || "?"} × {capture.height || "?"} · {captureSize}
            </span>
          ) : null}
        </span>
      </div>
      <button
        aria-label={`Télécharger le PNG de l’alerte ${alertTypeLabel}`}
        className={joinClasses([SECONDARY_ACTION_CLASS_NAME, "h-7 shrink-0 px-2.5 py-0 text-[0.68rem]"])}
        disabled={isDownloading}
        onClick={() => onDownload(capture)}
        title="Télécharger le PNG"
        type="button"
      >
        {isDownloading ? <LoaderCircle className="animate-spin" size={13} aria-hidden="true" /> : <Download size={13} aria-hidden="true" />}
        PNG
      </button>
      {contextPresentation ? (
        <details
          className="group w-full rounded-[4px] border border-slate-200 bg-slate-50/70"
          data-dispatch-alert-capture-context-details="true"
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-[4px] border border-slate-300 bg-white px-3 py-2 text-[0.7rem] font-bold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent [&::-webkit-details-marker]:hidden">
            <span>Voir le contexte</span>
            <ChevronDown className="shrink-0 transition-transform group-open:rotate-180" size={14} aria-hidden="true" />
          </summary>
          <div className="grid gap-3 border-t border-slate-200 px-3 py-3 lg:grid-cols-2">
            <section aria-labelledby={`capture-${capture.id}-circumstances`}>
              <h3
                className="m-0 text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-slate-600"
                id={`capture-${capture.id}-circumstances`}
              >
                Circonstances
              </h3>
              <dl className="mt-2 grid gap-1.5">
                {contextPresentation.circumstances.map((row, index) => (
                  <div className="grid gap-1 text-[0.68rem] leading-4 sm:grid-cols-[9.5rem_minmax(0,1fr)]" key={`${row.label}-${index}`}>
                    <dt className="font-semibold text-slate-500">{row.label}</dt>
                    <dd className="m-0 min-w-0 break-words text-slate-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
            {contextPresentation.technicalRows.length ? (
              <section aria-labelledby={`capture-${capture.id}-technical`}>
                <h3
                  className="m-0 text-[0.68rem] font-extrabold uppercase tracking-[0.08em] text-slate-600"
                  id={`capture-${capture.id}-technical`}
                >
                  Diagnostic technique
                </h3>
                <dl className="mt-2 grid gap-1.5">
                  {contextPresentation.technicalRows.map((row, index) => (
                    <div className="grid gap-1 text-[0.68rem] leading-4 sm:grid-cols-[9.5rem_minmax(0,1fr)]" key={`${row.label}-${index}`}>
                      <dt className="font-semibold text-slate-500">{row.label}</dt>
                      <dd className="m-0 min-w-0 break-all font-mono text-slate-800">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}
            <div className="flex items-center gap-2 lg:col-span-2">
              <button
                className={joinClasses([
                  "inline-flex min-h-8 items-center justify-center gap-1.5 rounded-[4px] border px-2.5 text-[0.68rem] font-bold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700",
                  copyStatus === "copied"
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-emerald-800 bg-[var(--color-brand)] text-white hover:bg-emerald-800",
                ])}
                data-dispatch-alert-capture-copy-context="true"
                onClick={handleCopyContext}
                type="button"
              >
                {copyStatus === "copied" ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                {copyStatus === "copied" ? "Contexte copié" : copyStatus === "error" ? "Réessayer la copie" : "Copier le contexte"}
              </button>
              <span aria-live="polite" className="text-[0.65rem] font-medium text-slate-500">
                {copyStatus === "error" ? "La copie automatique n’est pas disponible dans ce navigateur." : ""}
              </span>
            </div>
          </div>
        </details>
      ) : null}
    </article>
  );
}

export default function DispatchAlertCapturesPage({ embedded = false } = {}) {
  const [filters, setFilters] = React.useState({
    alertType: "",
    campaignId: "",
    dispatchDate: "",
    userId: "",
  });
  const [filterDraft, setFilterDraft] = React.useState({
    alertType: "",
    campaignId: "",
    dispatchDate: "",
    userId: "",
  });
  const [page, setPage] = React.useState(1);
  const [capturesState, setCapturesState] = React.useState(null);
  const [historyCaptureCount, setHistoryCaptureCount] = React.useState(null);
  const [summaryIsActive, setSummaryIsActive] = React.useState(null);
  const [targetUsers, setTargetUsers] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isClearing, setIsClearing] = React.useState(false);
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = React.useState(false);
  const [downloadingCaptureId, setDownloadingCaptureId] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const [actionFeedback, setActionFeedback] = React.useState(null);
  const requestSequenceRef = React.useRef(0);

  const loadCaptures = React.useCallback(async ({ quiet = false } = {}) => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!quiet) {
      setIsLoading(true);
    }
    setLoadError("");

    try {
      const payload = await fetchDispatchAlertCaptures({
        ...filters,
        page,
        pageSize: 25,
      });
      if (requestSequence === requestSequenceRef.current) {
        setCapturesState(payload);
      }
    } catch (error) {
      if (requestSequence === requestSequenceRef.current) {
        setLoadError(error instanceof Error ? error.message : "Impossible de charger les captures.");
      }
    } finally {
      if (requestSequence === requestSequenceRef.current && !quiet) {
        setIsLoading(false);
      }
    }
  }, [filters, page]);

  React.useEffect(() => {
    void loadCaptures();
  }, [loadCaptures]);

  const loadHistorySummary = React.useCallback(async () => {
    try {
      const config = await fetchDispatchAlertCaptureConfig();
      setHistoryCaptureCount(Number.isFinite(Number(config?.capture_count)) ? Number(config.capture_count) : 0);
      setSummaryIsActive(config?.is_active === true);
    } catch {
      // La liste filtrée reste exploitable même si le résumé global n'est pas disponible.
    }
  }, []);

  React.useEffect(() => {
    void loadHistorySummary();
  }, [loadHistorySummary]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let intervalId = null;
    const refreshQuietly = () => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        void loadCaptures({ quiet: true });
        void loadHistorySummary();
      }
    };
    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        intervalId = window.setInterval(refreshQuietly, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
      }
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      refreshQuietly();
      startPollingInterval();
    };

    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loadCaptures, loadHistorySummary]);

  React.useEffect(() => {
    let isCancelled = false;
    void fetchDispatchAlertCaptureTargets()
      .then((payload) => {
        if (!isCancelled) {
          setTargetUsers(normalizeTargetUsers(payload));
        }
      })
      .catch(() => {
        // La liste demeure exploitable avec les utilisateurs présents dans les captures.
      });
    return () => {
      isCancelled = true;
    };
  }, []);

  const captures = Array.isArray(capturesState?.items) ? capturesState.items : [];
  const groupedCaptures = React.useMemo(
    () => groupDispatchAlertCaptures(captures),
    [captures],
  );
  const capturesByActivityDate = React.useMemo(
    () => groupCaptureItemsByActivityDate(
      groupedCaptures,
      (capture) => capture?.occurred_at || capture?.created_at,
    ),
    [groupedCaptures],
  );
  const captureKeys = React.useMemo(
    () => groupedCaptures.map((capture) => buildAlertCaptureKey(capture)),
    [groupedCaptures],
  );
  const highlightedKeys = useDispatchCaptureArrivalHighlight(captureKeys, {
    enabled: capturesState != null,
    onArrival: () => {
      publishDispatchCaptureArrival("errors");
    },
    resetKey: `${page}|${filters.alertType}|${filters.campaignId}|${filters.dispatchDate}|${filters.userId}`,
    view: "errors",
  });
  const total = Number.isFinite(Number(capturesState?.total)) ? Number(capturesState.total) : 0;
  const pageSize = Number.isFinite(Number(capturesState?.page_size)) ? Number(capturesState.page_size) : 25;
  const currentPage = Number.isFinite(Number(capturesState?.page)) ? Number(capturesState.page) : page;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const isCollectionActive = typeof capturesState?.is_active === "boolean"
    ? capturesState.is_active
    : summaryIsActive === true;
  const hasCaptureHistory = historyCaptureCount == null ? total > 0 : historyCaptureCount > 0;
  const facetUsers = React.useMemo(
    () => normalizeCaptureFacetUsers(capturesState),
    [capturesState],
  );
  const userOptions = React.useMemo(
    () => buildCaptureUserFilterOptions({
      captures,
      facetUsers,
      targetUsers,
    }),
    [captures, facetUsers, targetUsers],
  );
  const campaignOptions = React.useMemo(
    () => buildCaptureCampaignFilterOptions(capturesState, captures),
    [captures, capturesState],
  );

  const updateFilterDraft = React.useCallback((key, value) => {
    setFilterDraft((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  }, []);

  const handleApplyFilters = React.useCallback((event) => {
    event.preventDefault();
    setPage(1);
    setFilters({ ...filterDraft });
  }, [filterDraft]);

  const handleResetFilters = React.useCallback(() => {
    const emptyFilters = {
      alertType: "",
      campaignId: "",
      dispatchDate: "",
      userId: "",
    };
    setFilterDraft(emptyFilters);
    setPage(1);
    setFilters(emptyFilters);
  }, []);

  const handleDownload = React.useCallback(async (capture) => {
    if (!capture?.id || downloadingCaptureId) {
      return;
    }
    setDownloadingCaptureId(capture.id);
    setActionFeedback(null);
    let objectUrl = "";
    try {
      const imageBlob = await fetchDispatchAlertCaptureImage(capture.id, { download: true });
      objectUrl = URL.createObjectURL(imageBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = buildCaptureFilename(capture);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setActionFeedback({
        message: error instanceof Error ? error.message : "Impossible de télécharger cette capture.",
        tone: "danger",
      });
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setDownloadingCaptureId("");
    }
  }, [downloadingCaptureId]);

  const handleClearHistory = React.useCallback(async () => {
    if (isCollectionActive || isClearing) {
      return;
    }

    setIsClearing(true);
    setActionFeedback(null);
    try {
      const payload = await clearDispatchAlertCaptures();
      setIsClearConfirmationOpen(false);
      setHistoryCaptureCount(0);
      setPage(1);
      setActionFeedback({
        message: `${Number(payload?.deleted_count) || 0} capture${Number(payload?.deleted_count) > 1 ? "s" : ""} supprimée${Number(payload?.deleted_count) > 1 ? "s" : ""}.`,
        tone: "success",
      });
      await loadCaptures();
    } catch (error) {
      setActionFeedback({
        message: error instanceof Error ? error.message : "Impossible d’effacer l’historique.",
        tone: "danger",
      });
    } finally {
      setIsClearing(false);
    }
  }, [isClearing, isCollectionActive, loadCaptures]);

  const actionFeedbackClassName = actionFeedback?.tone === "danger"
    ? statusToneClasses.danger
    : statusToneClasses.success;

  return (
    <div
      className={embedded
        ? "grid max-w-full min-h-min min-w-0 gap-3 overflow-x-clip"
        : joinClasses(["mx-auto grid w-full max-w-full min-h-min min-w-0 gap-3 overflow-x-clip px-4 pb-6 pt-0 max-[720px]:px-4", SYSTEM_CONFIG_SURFACE_WIDTH_CLASS_NAME])}
      data-dispatch-alert-captures-page="true"
    >
      {embedded ? null : (
      <WorkspacePageHeader
        icon={Camera}
        actions={<DispatchCaptureHistorySwitcher active="errors" />}
        className="sticky top-0 z-30 self-start"
        description="Historique privé des PNG recadrés sur les alertes rouges vues par les utilisateurs ciblés dans le Dispatch."
        title="Captures des alertes Dispatch"
      />
      )}

      <section className="grid gap-3 rounded-[22px] border border-black/6 bg-white px-4 py-3.5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
        <form className="grid gap-2" onSubmit={handleApplyFilters}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={joinClasses([
                "inline-flex items-center rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em]",
                isCollectionActive ? pillToneClasses.success : pillToneClasses.outline,
              ])}>
                Collecte {isCollectionActive ? "active" : "arrêtée"}
              </span>
              <span className="text-[0.8rem] font-medium text-slate-600">
                {total} résultat{total > 1 ? "s" : ""} selon les filtres
                {historyCaptureCount != null ? ` · ${historyCaptureCount} dans l’historique complet` : ""}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <button
                className={COMPACT_DANGER_ACTION_CLASS_NAME}
                disabled={isCollectionActive || isLoading || isClearing || !hasCaptureHistory}
                onClick={() => {
                  setActionFeedback(null);
                  setIsClearConfirmationOpen(true);
                }}
                title={isCollectionActive ? "Arrêtez d’abord la collecte dans Captures." : undefined}
                type="button"
              >
                <Trash2 size={13} aria-hidden="true" />
                Effacer l’historique
              </button>
              <button className={COMPACT_SECONDARY_ACTION_CLASS_NAME} onClick={handleResetFilters} type="button">
                Réinitialiser
              </button>
              <button className={COMPACT_PRIMARY_ACTION_CLASS_NAME} disabled={isLoading} type="submit">
                <Filter size={12} aria-hidden="true" />
                Appliquer les filtres
              </button>
            </div>
          </div>

          {actionFeedback ? (
            <p className={joinClasses(["m-0 rounded-[16px] px-3 py-2 text-[0.78rem] font-medium", actionFeedbackClassName])} role="status">
              {actionFeedback.message}
            </p>
          ) : null}

          <div className="grid gap-2 rounded-[18px] border border-slate-200 bg-slate-50/70 p-3">
            <div className="flex items-center gap-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-slate-600">
              <Filter size={14} aria-hidden="true" />
              Filtres
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Campagne
              <input
                className="h-9 min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                list="dispatch-alert-campaigns"
                onChange={(event) => updateFilterDraft("campaignId", event.target.value)}
                placeholder="Toutes les campagnes"
                value={filterDraft.campaignId}
              />
              <datalist id="dispatch-alert-campaigns">
                {campaignOptions.map((campaignId) => <option key={campaignId} value={campaignId} />)}
              </datalist>
            </label>

            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Utilisateur
              <select
                className="h-9 min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("userId", event.target.value)}
                value={filterDraft.userId}
              >
                <option value="">Tous les utilisateurs</option>
                {userOptions.map((user) => <option key={user.userId} value={user.userId}>{user.label}</option>)}
              </select>
            </label>

            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Journée de dispatch
              <input
                className="h-9 min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("dispatchDate", event.target.value)}
                type="date"
                value={filterDraft.dispatchDate}
              />
            </label>

            <label className="grid gap-1 text-[0.7rem] font-semibold text-slate-600">
              Type d’alerte
              <select
                className="h-9 min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-[0.78rem] font-normal text-slate-900 outline-hidden focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle"
                onChange={(event) => updateFilterDraft("alertType", event.target.value)}
                value={filterDraft.alertType}
              >
                <option value="">Tous les types</option>
                {ALERT_TYPES.map((alertType) => <option key={alertType.value} value={alertType.value}>{alertType.label}</option>)}
              </select>
            </label>
          </div>
          </div>
        </form>
      </section>

      {loadError ? (
        <section className="grid gap-3 rounded-[22px] border border-rose-200 bg-white px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <p className="m-0 text-[0.9rem] font-semibold text-rose-700">{loadError}</p>
          <div>
            <button className={SECONDARY_ACTION_CLASS_NAME} onClick={() => void loadCaptures()} type="button">
              <RefreshCcw size={15} aria-hidden="true" />
              Réessayer
            </button>
          </div>
        </section>
      ) : isLoading && !capturesState ? (
        <section className="flex min-h-52 items-center justify-center gap-3 rounded-[22px] border border-black/6 bg-white text-[0.86rem] font-medium text-slate-600 shadow-[0_14px_34px_rgba(15,23,42,0.05)]">
          <LoaderCircle className="animate-spin" size={18} aria-hidden="true" />
          Chargement des captures…
        </section>
      ) : groupedCaptures.length > 0 ? (
        <section
          className="grid max-w-full min-h-min min-w-0 gap-6 overflow-x-clip"
          aria-label="Historique des captures"
          data-dispatch-alert-capture-history="true"
        >
          {capturesByActivityDate.map((group) => (
            <div
              className="grid max-w-full min-h-min min-w-0 gap-2 overflow-x-clip rounded-[18px] border border-slate-200 bg-slate-50/90 p-2.5"
              data-dispatch-capture-dispatch-group="true"
              key={group.activityDate || "unspecified"}
            >
              <DispatchCaptureDispatchGroupHeader
                count={group.items.length}
                countLabel={`${group.items.length} capture${group.items.length > 1 ? "s" : ""}`}
                label={formatCaptureActivityGroupLabel(group.activityDate)}
              />
              {group.items.map((capture) => (
                <AlertCaptureCard
                  capture={capture}
                  isDownloading={downloadingCaptureId === capture.id}
                  isNew={highlightedKeys.has(buildAlertCaptureKey(capture))}
                  key={capture.id}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          ))}
        </section>
      ) : (
        <section className="grid min-h-52 place-items-center rounded-[22px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center shadow-[0_14px_34px_rgba(15,23,42,0.04)]">
          <div className="grid max-w-lg gap-2">
            <Camera className="mx-auto text-slate-400" size={30} aria-hidden="true" />
            <p className="m-0 text-[1rem] font-semibold text-slate-900">Aucune capture à afficher</p>
            <p className="m-0 text-[0.82rem] leading-5 text-slate-600">
              Activez la collecte dans Captures, ou modifiez les filtres si des captures existent déjà.
            </p>
          </div>
        </section>
      )}

      {total > 0 ? (
        <nav className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-black/6 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]" aria-label="Pagination des captures">
          <span className="text-[0.76rem] font-medium text-slate-600">
            Page {currentPage} sur {pageCount} · {total} résultat{total > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <button className={SECONDARY_ACTION_CLASS_NAME} disabled={isLoading || currentPage <= 1} onClick={() => setPage((currentValue) => Math.max(1, currentValue - 1))} type="button">
              Précédente
            </button>
            <button
              className={SECONDARY_ACTION_CLASS_NAME}
              disabled={isLoading || capturesState?.has_more !== true}
              onClick={() => setPage((currentValue) => currentValue + 1)}
              type="button"
            >
              Suivante
            </button>
          </div>
        </nav>
      ) : null}

      <ConfirmationModal
        confirmCheckbox={{
          description: "Cette suppression est définitive et touche toutes les campagnes affichées ou non par les filtres.",
          key: "confirm-clear-dispatch-alert-captures",
          label: "Je confirme vouloir supprimer tous les PNG et leurs métadonnées.",
          required: true,
        }}
        confirmLabel={isClearing ? "Suppression…" : "Effacer définitivement"}
        description="La collecte doit être arrêtée. Cette action ne modifie pas sa configuration ni les utilisateurs ciblés."
        errorMessage={actionFeedback?.tone === "danger" ? actionFeedback.message : ""}
        isOpen={isClearConfirmationOpen}
        onCancel={() => {
          if (!isClearing) {
            setIsClearConfirmationOpen(false);
          }
        }}
        onConfirm={handleClearHistory}
        summaryItems={[
          { label: "Captures", value: "Historique complet", tone: "warning" },
          { label: "Portée", value: "Toutes les campagnes, filtres ignorés", tone: "neutral" },
        ]}
        title="Effacer tout l’historique des alertes ?"
        tone="danger"
        warningMessage="Les images supprimées ne pourront pas être récupérées."
      />
    </div>
  );
}
